/**
 * شاشة «تحت المراجعة» — الحساب اتسجّل ولسه الإدارة ما اعتمدتوش.
 * (docs/09-التسجيل-الذاتي-والاعتماد.md · خطوة ٤).
 *
 * ⛔ **مفيش سايدبار ولا شريط علوي ولا أي بيانات** — الملحق بالحرف:
 *    «صفر بيانات، صفر مسارات، صفر شيء». الحساب المعلّق ما بيشوفش الشِل أصلًا،
 *    فحتى لو ظهرت له قايمة تنقّل مقفولة ده تسريب لهيكل السيستم بلا داعي.
 *
 * الحاجة الوحيدة اللي يقدر يعملها هنا: يخرج.
 */
import { useState } from "react";
import { Icon } from "@/components/Icon";
import { useSession } from "@/lib/session";
import { accountStatusOf } from "@/api/auth";
import "@/styles/auth.css";

export function Pending() {
  const { me, signOut } = useSession();
  const [busy, setBusy] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);

  const status = accountStatusOf(me);
  const email = me?.user.email ?? null;
  const name = me?.user.full_name_ar ?? null;

  // الفرق مهم: «أكّد بريدك» فعل عليه هو · «مستني الإدارة» فعل على غيره
  const awaitingEmail = status === "pending_email";

  async function onSignOut() {
    // قفل ضد الدوس المزدوج
    if (busy) return;
    setBusy(true);
    setSignOutError(null);
    // ⚠️ `signOut` بيمسح الحالة محليًا مهما حصل — حتى لو الطلب ما وصلش
    //    للسيستم — وبيسيب رسالة صريحة تظهر على شاشة الدخول. الحالة اللي
    //    اتفادينها: النت يقطع ⇒ الشاشة تهتز ⇒ الموظف يفضل داخل بلا أي كلمة.
    try {
      await signOut();
      // بننجح ⇒ الحالة بقت «زائر» والتوجيه بيوديه شاشة الدخول لوحده،
      // فما بنرجّعش `busy` عشان الزرار ما يبقاش قابل للدوس في اللحظة دي.
    } catch (err) {
      // ⛔ ممنوع الفشل الصامت: لو `signOut` نفسه اتكسر (عطل في الكود مش في
      //    الشبكة) الموظف لازم يعرف إنه لسه داخل بدل ما يمشي فاكر إنه خرج.
      console.error("[pending] تسجيل الخروج اتكسر بشكل مش متوقع", err);
      setSignOutError("مقدرناش نخرّجك دلوقتي — جرّب تاني، ولو الجهاز مش بتاعك بلّغ إدارة الأكاديمية");
      setBusy(false);
    }
  }

  return (
    <div
      dir="rtl"
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
      }}
    >
      <div
        className="card"
        style={{
          width: 480,
          maxWidth: "100%",
          padding: 32,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 16,
        }}
      >
        <div className="auth-brand" style={{ justifyContent: "center" }}>
          <div className="brand-mark">
            <Icon name="book" size={20} stroke="#0F1D33" />
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="brand-name">رتّل</div>
            <div className="brand-sub">السيستم الإداري</div>
          </div>
        </div>

        <span className="state-mark">
          <Icon name={awaitingEmail ? "mail" : "info"} size={17} />
        </span>

        {awaitingEmail ? (
          <>
            <div className="sec-title">أكّد بريدك الأول</div>
            <div className="t-desc" style={{ maxWidth: 340 }}>
              بعتنا رابط تأكيد على بريدك. افتح الرابط، وبعدها طلبك بيروح للإدارة
              للمراجعة.
            </div>
          </>
        ) : (
          <>
            <div className="sec-title">حسابك اتسجّل وتحت المراجعة</div>
            <div className="t-desc" style={{ maxWidth: 340 }}>
              هيوصلك إشعار أول ما يتفعّل. الإدارة هي اللي بتحدد قسمك ودورك.
            </div>
          </>
        )}

        {name ? (
          <div className="t-meta">
            داخل باسم: {name}
            {/* dir="ltr" هنا مسموح: بريد لاتيني صافي في عنصر لوحده */}
            {email ? (
              <>
                {" · "}
                <span dir="ltr">{email}</span>
              </>
            ) : null}
          </div>
        ) : null}

        {signOutError ? (
          <div className="err-box" role="alert" aria-live="assertive" style={{ width: "100%" }}>
            <Icon name="alert" size={18} stroke="#B42318" />
            <span className="t-body" style={{ color: "var(--danger)", fontWeight: 400 }}>
              {signOutError}
            </span>
          </div>
        ) : null}

        <button
          type="button"
          className={`btn ghost${busy ? " is-busy" : ""}`}
          onClick={() => void onSignOut()}
          disabled={busy}
          style={{ marginTop: 8 }}
        >
          {busy ? (
            <>
              <Icon name="spinner" size={16} strokeWidth={1.9} />
              بيخرّج…
            </>
          ) : (
            <>
              <Icon name="logout" size={16} />
              تسجيل الخروج
            </>
          )}
        </button>

        <div className="t-meta">استعجلت؟ برجاء التواصل مع إدارة الأكاديمية</div>
      </div>
    </div>
  );
}
